import useBodyScrollLock from '../hooks/useBodyScrollLock';
import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useLocation,
  useHistory,
  useParams
} from "react-router-dom";
import { useAtom } from 'jotai';
import { isAuthAtom, userRoleAtom } from '../Services/store/atoms';
import RouteCheck from './RouteCheck';
import ScrollToTop from '../Components/ScrollToTop';
import Spinner from '../Components/Spinner';
import PendingPackSuggestionsModal from '../Components/PendingPackSuggestionsModal';
import AgeVerificationModal from '../Components/AgeVerificationModal';
import { DashboardShell } from '../Layouts/DashboardContext';
import { HiX } from 'react-icons/hi';
import axios from 'axios';
import config from '../Config';

// Importaciones inmediatas para páginas críticas (Landing, Login, Register)
import Landing from '../Pages/Landing';
import Home from '../Pages/Home';
import LandingWithoutHeader from '../Pages/admin/LandingWithoutHeader';
import FeedPublicWithoutHeader from '../Pages/admin/FeedPublicWithoutHeader';
import ListPacksWithoutHeader from '../Pages/admin/ListPacksWithoutHeader';
import Login from '../Pages/Login';
import Register from '../Pages/Register';
import ReferralCapture from '../Pages/ReferralCapture';
import ModelCompleteRegistration from '../Pages/model/CompleteRegistration';
import ClientCompleteProfile from '../Pages/client/CompleteProfile';
import ChooseRole from '../Pages/ChooseRole';
import ListPacksHome from '../Pages/packs/ListPacks';
import FeedPublic from '../Pages/FeedPublic';
import FeedByUser from '../Pages/FeedByUser';
import ProfileModel from '../Pages/ProfileModel';
import PublicLayout from '../Layouts/PublicLayout';
import NicknameResolver from '../Components/NicknameResolver';
import FluidMediaModal from '../Components/FluidMediaModal2';

// Lazy loading para layouts de dashboard (solo carga inicial)
const AdminLayout = lazy(() => import('../Layouts/Admin/Layout'));
const ModelLayout = lazy(() => import('../Layouts/Model/Layout'));
const ClientLayout = lazy(() => import('../Layouts/Client/Layout'));
const UserLayout = lazy(() => import('../Layouts/User/Layout'));

// Páginas del dashboard: lazy-loaded por rol para que el bundle inicial no
// cargue admin/modelo/cliente/usuario de una sola vez (antes estaban como
// import estático, lo que anulaba el code-splitting de los Layouts de abajo
// y generaba un chunk único de ~4MB). DashboardRoute ya envuelve todo esto
// en un <Suspense fallback={<Spinner />}>, así que el flicker que la
// importación directa buscaba evitar queda cubierto por ese fallback.
const HomeModel = lazy(() => import('../Pages/model/HomeModel'));
const HomeUser = lazy(() => import('../Pages/user/HomeUser'));
const HomeClient = lazy(() => import('../Pages/client/HomeClient'));
const HomeAdmin = lazy(() => import('../Pages/admin/HomeAdmin'));
const WhatsNew = lazy(() => import('../Pages/admin/WhatsNew'));
const VerifyFails = lazy(() => import('../Pages/admin/VerifyFails'));
const VerifyApprove = lazy(() => import('../Pages/admin/VerifyApprove'));
const VerifyDocumentUpdates = lazy(() => import('../Pages/admin/VerifyDocumentUpdates'));
const UploadPack = lazy(() => import('../Pages/model/packs/UploadPack'));
const ListPacks = lazy(() => import('../Pages/model/packs/ListPacks'));
const EditPack = lazy(() => import('../Pages/model/packs/EditPack'));
const ListPacksVerify = lazy(() => import('../Pages/admin/packs/ListPacksVerify'));
const AdminUploadPack = lazy(() => import('../Pages/admin/packs/AdminUploadPack'));
const Profile = lazy(() => import('../Pages/model/Profile/Profile'));
const ListProfileVerify = lazy(() => import('../Pages/admin/profiles/ListProfilesVerify'));
const BuyPack = lazy(() => import('../Pages/packs/BuyPacks'));
const FavoriteModel = lazy(() => import('../Pages/client/model/favorites'));
const PurcharsedPacks = lazy(() => import('../Pages/client/packs/purchased'));
const Sales = lazy(() => import('../Pages/model/Sales'));
const ListFeed = lazy(() => import('../Pages/model/feed/ListFeed'));
const RechargeCoins = lazy(() => import('../Pages/client/coins/RechargeCoin'));
const PaymentStatus = lazy(() => import('../Pages/client/coins/PaymentStatus'));
const ModelStats = lazy(() => import('../Pages/model/ModelStats'));
const ModelStatsProcessing = lazy(() => import('../Pages/model/ModelStatsProcessing'));
const PaymentMethods = lazy(() => import('../Pages/model/Profile/PaymentMethods'));
const DocumentsUpdate = lazy(() => import('../Pages/model/Profile/DocumentsUpdate'));
const ReferralLink = lazy(() => import('../Pages/model/ReferralLink'));
const SalesModel = lazy(() => import('../Pages/model/Profile/SalesModel'));
const ModelSalesAdmin = lazy(() => import('../Pages/admin/sales/ModelSalesAdmin'));
const ChatClaims = lazy(() => import('../Pages/admin/claims/ChatClaims'));
const BlockAlerts = lazy(() => import('../Pages/admin/claims/BlockAlerts'));
const FeedReview = lazy(() => import('../Pages/admin/claims/FeedReview'));
const ImageModerations = lazy(() => import('../Pages/admin/claims/ImageModerations'));
const BlockedUsers = lazy(() => import('../Pages/user/BlockedUsers'));
const ActiveSessions = lazy(() => import('../Pages/user/ActiveSessions'));
const NotificationPreferences = lazy(() => import('../Pages/user/NotificationPreferences'));
const Notifications = lazy(() => import('../Pages/Notifications'));
const FeedPublicFake = lazy(() => import('../Pages/FeedPublicFake'));
const CountryBlocking = lazy(() => import('../Pages/admin/CountryBlocking'));
const PasswordChange = lazy(() => import('../Pages/admin/PasswordChange'));
const UserManagement = lazy(() => import('../Pages/admin/UserManagement'));
const ReferralManagement = lazy(() => import('../Pages/admin/ReferralManagement'));
const SearchPacks = lazy(() => import('../Pages/admin/SearchPacks'));
const ModelPacks = lazy(() => import('../Pages/admin/ModelPacks'));
const ShareModels = lazy(() => import('../Pages/admin/ShareModels'));
const Conversations = lazy(() => import('../Pages/admin/Conversations'));
const MatchNotices = lazy(() => import('../Pages/admin/MatchNotices'));
const Stats = lazy(() => import('../Pages/admin/Stats'));
const TestTools = lazy(() => import('../Pages/admin/TestTools'));
const PackPurchasesAdmin = lazy(() => import('../Pages/admin/finance/PackPurchasesAdmin'));
const UserRechargesAdmin = lazy(() => import('../Pages/admin/finance/UserRechargesAdmin'));
const EmailCampaigns = lazy(() => import('../Pages/admin/EmailCampaigns'));
const ChatScreen = lazy(() => import('../Pages/ChatScreen'));

// Dashboard layout persistente: layout fijo, solo cambia el contenido
const DashboardRoute = ({ layout: Layout, children }) => {
  const location = useLocation();
  const background = location.state?.background;

  return (
    <Suspense fallback={<Spinner />}>
      <Layout>
        <DashboardShell>
          <Switch location={background || location}>
            {children}
          </Switch>
        </DashboardShell>
      </Layout>
    </Suspense>
  );
};

function ProfileModalPortal({ onClose }) {
  return createPortal(
    <>
      <style>{`
        @media (min-width: 640px) {
          .profile-modal-card { height: calc(100vh - 7.5rem); }
        }
      `}</style>
      <div
        className="fixed inset-x-0 bottom-0 top-0 sm:top-14 bg-black/60 flex flex-col sm:flex-row sm:items-start sm:justify-center"
        // Por encima del header (10000) y del panel lateral (10002): con 2000 el
        // header tapaba en el celular la barra del modal con la X de cerrar.
        style={{ zIndex: 10005 }}
        onClick={onClose}
      >
        <div
          className="profile-modal-card ap-modal-surface relative w-full flex-1 flex flex-col overflow-hidden rounded-none sm:flex-none sm:max-w-4xl sm:my-8 sm:mx-4 sm:rounded-2xl bg-white dark:bg-gray-800 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header. En mobile el modal ahora ocupa toda la pantalla
              (incluso lo que antes dejaba libre para el header de la app),
              así que esta barra toca el borde real — necesita el padding de
              safe-area para no quedar debajo del notch/isla dinámica. */}
          <div
            className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sm:rounded-t-2xl"
            style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
          >
            <span className="text-base font-semibold text-gray-700 dark:text-gray-300">Model Profile</span>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors"
              aria-label="Close"
            >
              <HiX className="w-5 h-5" />
            </button>
          </div>

          {/* Ya no scrollea acá: ProfileModel arma su propio layout interno
              (header fijo + una tab activa por vez, cada una con su propio
              scroll independiente en overflow-y-auto), así que este div solo
              necesita darle una altura definida vía flex-1 + overflow-hidden. */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <ProfileModel inModal />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// Entrada directa a /client/profileModal/:id (links de correos, URL pegada):
// sin `background` en el state esa ruta no muestra nada, así que se reabre
// como modal sobre la lista de modelos, igual que hace NicknameResolver. Si no
// hay sesión se abre la versión pública en vez de mandar al inicio.
function DirectProfileModalEntry() {
  const { idModel } = useParams();
  const history = useHistory();
  const [isAuth] = useAtom(isAuthAtom);
  const [userRole] = useAtom(userRoleAtom);

  useEffect(() => {
    const dashboards = ['model', 'client', 'admin', 'user', 'assistant'];
    if (isAuth && dashboards.includes(userRole)) {
      history.replace(`/${userRole}/profileModal/${idModel}`, {
        background: { pathname: `/${userRole}/models`, search: '', hash: '', state: null }
      });
    } else {
      history.replace(`/profileModal/${idModel}`, {
        background: { pathname: '/', search: '', hash: '', state: null }
      });
    }
  }, [idModel, isAuth, userRole, history]);

  return <Spinner />;
}

function Navigator() {
  return (
    <Router>
      <ScrollToTop />
      {/* Dentro del Router: decide según la ruta (ver AgeVerificationModal). */}
      <AgeVerificationModal />
      <PendingPackSuggestionsModal />
      <NavigatorInner />
    </Router>
  );
}

function NavigatorInner() {
  const location = useLocation();
  const history = useHistory();
  const background = location.state?.background;

  const handleCloseModal = () => {
    if (background) {
      history.replace(background);
    } else {
      history.goBack();
    }
  };

  // El modal flotante del perfil bloquea el scroll mientras hay un `background`.
  useBodyScrollLock(!!background);

  useEffect(() => {
    if (!background) return;
    const onKey = (e) => { if (e.key === 'Escape') handleCloseModal(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [background, history]);

  const lastTracked = useRef('');
  const lastTrackedModal = useRef('');
  useEffect(() => {
    const path = location.pathname;

    // El modal de perfil (background truthy) se trackea con su propia referencia:
    // si usáramos lastTracked acá, al cerrarlo la URL vuelve a la página de fondo
    // (ya trackeada antes de abrir el modal) y ese path ya no coincidiría con
    // lastTracked (que quedó en el path del modal), generando una revisita falsa
    // de una página que el usuario nunca dejó.
    if (background) {
      if (path === lastTrackedModal.current) return;
      lastTrackedModal.current = path;
    } else {
      if (path === lastTracked.current) return;
      lastTracked.current = path;
    }

    const type = path.startsWith('/model/') ? 'model'
      : path.startsWith('/client/') ? 'client'
      : path.startsWith('/admin/') || path.startsWith('/assistant/') ? 'admin'
      : path.startsWith('/user/') ? 'user'
      : path.startsWith('/profileModal/') || path.startsWith('/FeedByUser/') ? 'profile'
      : path.startsWith('/packs') || path.startsWith('/listPacks') ? 'pack'
      : path.startsWith('/feed') || path.startsWith('/FeedPublic') ? 'feed'
      : 'page';

    const data = { type, url: window.location.href };
    if (type === 'profile') {
      const m = path.match(/\/(\d+)$/);
      if (m) data.visited_id = m[1];
    }

    axios.post(`${config.REACT_APP_BASE_URL}/api/v1/track-visit`, data, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 3000,
    }).catch(() => {});
  }, [location.pathname, background]);

  return (
    <>
      <Switch location={background || location}>
        {/* Home - Página pública principal (pastilla Modelos/Packs/Feeds) - accesible a todos */}
        <RouteCheck type="quest" path={"/"} exact component={Home} />
        <RouteCheck type="guest" path={"/login"} exact component={Login} />
        <RouteCheck type="guest" path={"/register"} exact component={Register} />
        {/* Link amigable para compartir: preselecciona la pestaña de registro correspondiente */}
        <RouteCheck type="guest" path={"/registermodel"} exact component={Register} />
        <RouteCheck type="guest" path={"/registerclient"} exact component={Register} />
        {/* Link de referido: guarda el código y redirige al registro */}
        <RouteCheck type="guest" path={"/r/:code"} exact component={ReferralCapture} />
        <RouteCheck type="guest" path={"/:code/register"} exact component={ReferralCapture} />
        <RouteCheck type="private" path={"/model/complete-registration"} exact component={ModelCompleteRegistration} />
        <RouteCheck type="private" path={"/choose-role"} exact component={ChooseRole} />

        {/* Packs público */}
        <RouteCheck type="quest" path={"/packs"} exact component={ListPacksWithoutHeader} />
        <RouteCheck type="guest" path={"/listPacks"} exact component={ListPacksHome} />
        
        {/* Feed público */}
        <RouteCheck type="quest" path={"/feed"} exact component={FeedPublicWithoutHeader} />
        <RouteCheck type="guest" path={"/FeedPublic"} exact component={FeedPublic} />

        {/* Dashboard Model */}
        <RouteCheck type="private" path={"/model"}>
          <DashboardRoute layout={ModelLayout}>
            <Route exact path="/model" component={HomeModel} />
            <Route exact path="/model/models" component={LandingWithoutHeader} />
            <Route exact path="/model/packs" component={ListPacksWithoutHeader} />
            <Route exact path="/model/feedPublic" component={FeedPublicWithoutHeader} />
            <Route exact path="/model/uploadpack" component={UploadPack} />
            <Route exact path="/model/listpacks" component={ListPacks} />
            <Route exact path="/model/packs/edit/:packId" component={EditPack} />
            <Route exact path="/model/profile" component={Profile} />
            <Route exact path="/model/sales" component={Sales} />
            <Route exact path="/model/totalsales" component={ModelStats} />
            <Route exact path="/model/totalsalesProcessing" component={ModelStatsProcessing} />
            <Route exact path="/model/feed" component={ListFeed} />
            <Route exact path="/model/paymentMethods" component={PaymentMethods} />
            <Route exact path="/model/documents" component={DocumentsUpdate} />
            <Route exact path="/model/referral" component={ReferralLink} />
            <Route exact path="/model/SalesModel" component={SalesModel} />
            <Route exact path="/model/chat" component={ChatScreen} />
            <Route exact path="/model/chat/:chatId" component={ChatScreen} />
            <Route exact path="/model/profileModal/:idModel" component={ProfileModel} />
            <Route exact path="/model/blocked" component={BlockedUsers} />
            <Route exact path="/model/sessions" component={ActiveSessions} />
            <Route exact path="/model/notifications" component={Notifications} />
            <Route exact path="/model/settings/sessions" component={ActiveSessions} />
            <Route exact path="/model/settings/notifications" component={NotificationPreferences} />
          </DashboardRoute>
        </RouteCheck>
        {/* Dashboard User */}
        <RouteCheck type="private" path={"/user"}>
          <DashboardRoute layout={UserLayout}>
            <Route exact path="/user" component={HomeUser} />
            <Route exact path="/user/models" component={LandingWithoutHeader} />
            <Route exact path="/user/packs" component={ListPacksWithoutHeader} />
            <Route exact path="/user/feed" component={FeedPublicWithoutHeader} />
            <Route exact path="/user/buyPack/:packId" component={BuyPack} />
            <Route exact path="/user/chat" component={ChatScreen} />
            <Route exact path="/user/chat/:chatId" component={ChatScreen} />
            <Route exact path="/user/profileModal/:idModel" component={ProfileModel} />
            <Route exact path="/user/blocked" component={BlockedUsers} />
            <Route exact path="/user/sessions" component={ActiveSessions} />
            <Route exact path="/user/notifications" component={Notifications} />
            <Route exact path="/user/settings/sessions" component={ActiveSessions} />
            <Route exact path="/user/settings/notifications" component={NotificationPreferences} />
          </DashboardRoute>
        </RouteCheck>

        {/* Dashboard Client */}
        <Route exact path="/client/profileModal/:idModel" component={DirectProfileModalEntry} />
        <RouteCheck type="private" path={"/client"}>
          <DashboardRoute layout={ClientLayout}>
            <Route exact path="/client" component={HomeClient} />
            {/* Onboarding: va adentro del shell para usar el layout de cliente.
                RouteCheck exime este pathname de su propia redirección, así que
                pasar por el guard de /client no genera loop. */}
            <Route exact path="/client/complete-profile" component={ClientCompleteProfile} />
            <Route exact path="/client/purchasedPacks" component={PurcharsedPacks} />
            <Route exact path="/client/packs" component={ListPacksWithoutHeader} />
            <Route exact path="/client/models" component={LandingWithoutHeader} />
            <Route exact path="/client/feed" component={FeedPublicWithoutHeader} />
            <Route exact path="/client/buyPack/:packId" component={BuyPack} />
            <Route exact path="/client/favoriteModels" component={FavoriteModel} />
            <Route exact path="/client/purchasedPacks/:packId?" component={PurcharsedPacks} />
            <Route exact path="/client/rechargeCoins" component={RechargeCoins} />
            <Route exact path="/client/paymentStatus" component={PaymentStatus} />
            <Route exact path="/client/chat" component={ChatScreen} />
            <Route exact path="/client/chat/:chatId" component={ChatScreen} />
            <Route exact path="/client/blocked" component={BlockedUsers} />
            <Route exact path="/client/sessions" component={ActiveSessions} />
            <Route exact path="/client/notifications" component={Notifications} />
            <Route exact path="/client/settings/sessions" component={ActiveSessions} />
            <Route exact path="/client/settings/notifications" component={NotificationPreferences} />
            <Route path="/client/profileModal/:idModel" render={() => null} />
          </DashboardRoute>
        </RouteCheck>
        <RouteCheck type="guest" path={"/FeedPublic"} exact component={FeedPublic} />
        <RouteCheck type="guest" path={"/FeedByUser/:idModel"} exact component={FeedByUser} />

        {/* Dashboard Admin (solo usuarios autenticados) */}
        <RouteCheck type="private" path={"/admin"}>
          <DashboardRoute layout={AdminLayout}>
            <Route exact path="/admin" component={HomeAdmin} />
            <Route exact path="/admin/whatsnew" component={WhatsNew} />
            <Route exact path="/admin/verifyFail" component={VerifyFails} />
            <Route exact path="/admin/verifyApprove" component={VerifyApprove} />
            <Route exact path="/admin/documentUpdates" component={VerifyDocumentUpdates} />
            <Route exact path="/admin/listpacksverify" component={ListPacksVerify} />
            <Route exact path="/admin/uploadPack" component={AdminUploadPack} />
            <Route exact path="/admin/listprofilesverify" component={ListProfileVerify} />
            <Route exact path="/admin/modelsales" component={ModelSalesAdmin} />
            <Route exact path="/admin/chat-claims" component={ChatClaims} />
            <Route exact path="/admin/block-alerts" component={BlockAlerts} />
            <Route exact path="/admin/feed-review" component={FeedReview} />
            <Route exact path="/admin/image-moderations" component={ImageModerations} />
            <Route exact path="/admin/FeedPublicFake" component={FeedPublicFake} />
            <Route exact path="/admin/countryBlocking" component={CountryBlocking} />
            <Route exact path="/admin/passwordChange" component={PasswordChange} />
            <Route exact path="/admin/userManagement" component={UserManagement} />
            <Route exact path="/admin/referrals" component={ReferralManagement} />
            <Route exact path="/admin/searchPacks" component={SearchPacks} />
            <Route exact path="/admin/modelPacks/:userId" component={ModelPacks} />
            <Route exact path="/admin/shareModels" component={ShareModels} />
            
            {/* Páginas Públicas dentro del Dashboard */}
            <Route exact path="/admin/models" component={LandingWithoutHeader} />
            <Route exact path="/admin/packs" component={ListPacksWithoutHeader} />
            <Route exact path="/admin/feed" component={FeedPublicWithoutHeader} />
            <Route exact path="/admin/profileModal/:idModel" component={ProfileModel} />
            <Route exact path="/admin/FeedByUser/:idModel" component={FeedByUser} />
            <Route exact path="/admin/chat" component={ChatScreen} />
            <Route exact path="/admin/chat/:chatId" component={ChatScreen} />
            <Route exact path="/admin/conversations" component={Conversations} />
            <Route exact path="/admin/match-notices" component={MatchNotices} />
            <Route exact path="/admin/stats" component={Stats} />
            <Route exact path="/admin/test-tools" component={TestTools} />
            <Route exact path="/admin/pack-purchases" component={PackPurchasesAdmin} />
            <Route exact path="/admin/user-recharges" component={UserRechargesAdmin} />
            <Route exact path="/admin/email-campaigns" component={EmailCampaigns} />
            <Route exact path="/admin/sessions" component={ActiveSessions} />
            <Route exact path="/admin/notifications" component={Notifications} />
            <Route exact path="/admin/settings/sessions" component={ActiveSessions} />
            <Route exact path="/admin/settings/notifications" component={NotificationPreferences} />
          </DashboardRoute>
        </RouteCheck>

        {/* Dashboard assistant */}
        <RouteCheck type="private" path={"/assistant"}>
          <DashboardRoute layout={AdminLayout}>
            <Route exact path="/assistant" component={HomeAdmin} />
            <Route exact path="/assistant/models" component={LandingWithoutHeader} />
            <Route exact path="/assistant/packs" component={ListPacksWithoutHeader} />
            <Route exact path="/assistant/feed" component={FeedPublicWithoutHeader} />
            <Route exact path="/assistant/verifyFail" component={VerifyFails} />
            <Route exact path="/assistant/verifyApprove" component={VerifyApprove} />
            <Route exact path="/assistant/documentUpdates" component={VerifyDocumentUpdates} />
            <Route exact path="/assistant/listpacksverify" component={ListPacksVerify} />
            <Route exact path="/assistant/listprofilesverify" component={ListProfileVerify} />
            <Route exact path="/assistant/modelsales" component={ModelSalesAdmin} />
            <Route exact path="/assistant/chat-claims" component={ChatClaims} />
            <Route exact path="/assistant/block-alerts" component={BlockAlerts} />
            <Route exact path="/assistant/searchPacks" component={SearchPacks} />
            <Route exact path="/assistant/passwordChange" component={PasswordChange} />
            <Route exact path="/assistant/userManagement" component={UserManagement} />
            <Route exact path="/assistant/modelPacks/:userId" component={ModelPacks} />
            <Route exact path="/assistant/profileModal/:idModel" component={ProfileModel} />
            <Route exact path="/assistant/chat" component={ChatScreen} />
            <Route exact path="/assistant/chat/:chatId" component={ChatScreen} />
            <Route exact path="/assistant/conversations" component={Conversations} />
            <Route exact path="/assistant/match-notices" component={MatchNotices} />
            <Route exact path="/assistant/stats" component={Stats} />
            <Route exact path="/assistant/test-tools" component={TestTools} />
            <Route exact path="/assistant/pack-purchases" component={PackPurchasesAdmin} />
            <Route exact path="/assistant/user-recharges" component={UserRechargesAdmin} />
            <Route exact path="/assistant/email-campaigns" component={EmailCampaigns} />
            <Route exact path="/assistant/sessions" component={ActiveSessions} />
            <Route exact path="/assistant/notifications" component={Notifications} />
            <Route exact path="/assistant/settings/sessions" component={ActiveSessions} />
            <Route exact path="/assistant/settings/notifications" component={NotificationPreferences} />
          </DashboardRoute>
        </RouteCheck>

        {/* Products <RouteCheck type="private" path={"/addProduct"} exact component={ProductAdd} /> */}

        {/* Fallback Route for Nickname Resolver */}
        <RouteCheck type="guest" path="/:nickname" exact component={NicknameResolver} />
      </Switch>

      {/* Modal flotante para perfil de modelo */}
      {background && (
        <>
          <Route path="/profileModal/:idModel" exact>
            {({ match }) => match && <ProfileModalPortal onClose={handleCloseModal} />}
          </Route>
          <Route path="/:section/profileModal/:idModel">
            {({ match }) => match && <ProfileModalPortal onClose={handleCloseModal} />}
          </Route>
          <Route path="/client/profileModal/:idModel">
            {({ match }) => match && <ProfileModalPortal onClose={handleCloseModal} />}
          </Route>
          <Route path="/user/profileModal/:idModel">
            {({ match }) => match && <ProfileModalPortal onClose={handleCloseModal} />}
          </Route>
        </>
      )}

      {/* Fluid Media Modal - al mismo nivel que ProfileModalPortal.
          Se abre con history.push (ver ProfileModel.handleMediaClick), así
          que cerrarlo es un goBack() que solo saca esa entrada del historial
          y no toca la del modal de perfil debajo. */}
      {location.state?.fluidMedia && (
        <FluidMediaModal
          show={true}
          onClose={() => history.goBack()}
          mediaUrls={location.state.fluidMedia}
        />
      )}
    </>
  );
}

export default Navigator;
